/**
 * i18n configuration
 * PearlBridge Voyages & Events multilingual support
 * Languages: English (default), active translation locales, Chinese (Mandarin), Swahili
 * Path-based language detection uses locale prefixes such as /en, /de, /fr, /sw, /zh
 *
 * @module i18n
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { SUPPORTED_LOCALES } from './config/localeConfig';
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import es from './locales/es.json';
import nl from './locales/nl.json';
import pt from './locales/pt.json';
import sv from './locales/sv.json';
import zh from './locales/zh.json';
import sw from './locales/sw.json';

const resources = {
  en: { translation: en },
  de: { translation: de },
  fr: { translation: fr },
  it: { translation: it },
  es: { translation: es },
  nl: { translation: nl },
  pt: { translation: pt },
  sv: { translation: sv },
  zh: { translation: zh },
  sw: { translation: sw },
  ...Object.fromEntries(
    SUPPORTED_LOCALES
      .filter((code) => !['en', 'de', 'fr', 'it', 'es', 'nl', 'pt', 'sv', 'zh', 'sw'].includes(code))
      .map((code) => [code, { translation: en }])
  ),
};

export const SUPPORTED_LANGUAGES = [...SUPPORTED_LOCALES];
export const DEFAULT_LANGUAGE = 'en';

export const LANGUAGE_CONFIG = {
  en: { label: 'English', flag: '🇺🇸', dir: 'ltr', hreflang: 'en-US', ogLocale: 'en_US' },
  de: { label: 'Deutsch', flag: '🇩🇪', dir: 'ltr', hreflang: 'de-DE', ogLocale: 'de_DE' },
  fr: { label: 'Français', flag: '🇫🇷', dir: 'ltr', hreflang: 'fr-FR', ogLocale: 'fr_FR' },
  it: { label: 'Italiano', flag: '🇮🇹', dir: 'ltr', hreflang: 'it-IT', ogLocale: 'it_IT' },
  es: { label: 'Español', flag: '🇪🇸', dir: 'ltr', hreflang: 'es-ES', ogLocale: 'es_ES' },
  pl: { label: 'Polski', flag: '🇵🇱', dir: 'ltr', hreflang: 'pl-PL', ogLocale: 'pl_PL' },
  sv: { label: 'Svenska', flag: '🇸🇪', dir: 'ltr', hreflang: 'sv-SE', ogLocale: 'sv_SE' },
  nl: { label: 'Nederlands', flag: '🇳🇱', dir: 'ltr', hreflang: 'nl-NL', ogLocale: 'nl_NL' },
  pt: { label: 'Português', flag: '🇵🇹', dir: 'ltr', hreflang: 'pt-PT', ogLocale: 'pt_PT' },
  el: { label: 'Ελληνικά', flag: '🇬🇷', dir: 'ltr', hreflang: 'el-GR', ogLocale: 'el_GR' },
  fi: { label: 'Suomi', flag: '🇫🇮', dir: 'ltr', hreflang: 'fi-FI', ogLocale: 'fi_FI' },
  da: { label: 'Dansk', flag: '🇩🇰', dir: 'ltr', hreflang: 'da-DK', ogLocale: 'da_DK' },
  hu: { label: 'Magyar', flag: '🇭🇺', dir: 'ltr', hreflang: 'hu-HU', ogLocale: 'hu_HU' },
  cs: { label: 'Čeština', flag: '🇨🇿', dir: 'ltr', hreflang: 'cs-CZ', ogLocale: 'cs_CZ' },
  sk: { label: 'Slovenčina', flag: '🇸🇰', dir: 'ltr', hreflang: 'sk-SK', ogLocale: 'sk_SK' },
  sl: { label: 'Slovenščina', flag: '🇸🇮', dir: 'ltr', hreflang: 'sl-SI', ogLocale: 'sl_SI' },
  et: { label: 'Eesti', flag: '🇪🇪', dir: 'ltr', hreflang: 'et-EE', ogLocale: 'et_EE' },
  lv: { label: 'Latviešu', flag: '🇱🇻', dir: 'ltr', hreflang: 'lv-LV', ogLocale: 'lv_LV' },
  lt: { label: 'Lietuvių', flag: '🇱🇹', dir: 'ltr', hreflang: 'lt-LT', ogLocale: 'lt_LT' },
  hr: { label: 'Hrvatski', flag: '🇭🇷', dir: 'ltr', hreflang: 'hr-HR', ogLocale: 'hr_HR' },
  ro: { label: 'Română', flag: '🇷🇴', dir: 'ltr', hreflang: 'ro-RO', ogLocale: 'ro_RO' },
  bg: { label: 'Български', flag: '🇧🇬', dir: 'ltr', hreflang: 'bg-BG', ogLocale: 'bg_BG' },
  sw: { label: 'Kiswahili', flag: '🇰🇪', dir: 'ltr', hreflang: 'sw-KE', ogLocale: 'sw_KE' },
  zh: { label: '中文', flag: '🇨🇳', dir: 'ltr', hreflang: 'zh-CN', ogLocale: 'zh_CN' },
};

export const PUBLIC_LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES;

/**
 * Custom path-based language detector
 * Detects language from URL path prefix: /en/..., /de/..., /sw/..., /zh/...
 */
const pathLanguageDetector = {
  name: 'path',
  lookup() {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
      return segments[0];
    }
    return undefined;
  },
  cacheUserLanguage(lng) {
    localStorage.setItem('afriBrightLang', lng);
  },
};

const languageDetectorInstance = new LanguageDetector();
languageDetectorInstance.addDetector(pathLanguageDetector);

i18n
  .use(languageDetectorInstance)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['path', 'cookie', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
      lookupLocalStorage: 'afriBrightLang',
      lookupCookie: 'lang',
      cookieOptions: { path: '/', sameSite: 'lax' },
    },
    react: {
      useSuspense: false,
    },
    ...(import.meta.env?.DEV && {
      saveMissing: true,
      missingKeyHandler(lngs, ns, key, fallbackValue) {
        console.warn(
          `[i18n] Missing translation key: "${key}" for lang(s): ${lngs.join(', ')} — falling back to: "${fallbackValue}"`
        );
      },
    }),
  });

/**
 * Helper: Get localized path
 * Prepends language prefix to a path
 */
export const getLocalizedPath = (path, lang) => {
  const language = lang || i18n.language || DEFAULT_LANGUAGE;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `/${language}${cleanPath}`;
};

/**
 * Helper: Strip language prefix from path
 */
export const stripLanguagePrefix = (path) => {
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    return '/' + segments.slice(1).join('/');
  }
  return path;
};

/**
 * Helper: Get current language from path
 */
export const getLanguageFromPath = (path) => {
  const segments = path.split('/').filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
    return segments[0];
  }
  return DEFAULT_LANGUAGE;
};

export default i18n;

// Developed and Designed by Trophy Developers
